import { useEffect, useState } from 'react';
import { getClients, getSeasons } from '@/services/mockData';
import { DetailShell } from '../../detail/DetailShell';
import { Card } from '../../detail/Card';
import { SectionTitle } from '../../detail/SectionTitle';
import { Chip } from '../../detail/Chip';
import { Table, Th, Td } from '../../detail/Table';

const MOOD_BG = {
  steady:    'var(--paper-2)',
  stretched: 'var(--warn-soft)',
  light:     'var(--pos-soft)',
};

// Portfolio Management — active clients table, seasonal load chart, resource-building rows.

export function PortfolioDetail({ onClose }) {
  const [clients, setClients] = useState(null);
  const [seasons, setSeasons] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getClients(), getSeasons()])
      .then(([c, s]) => { if (!cancelled) { setClients(c); setSeasons(s); } })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <DetailShell eyebrow="06 · Delivery Dashboard" title="Portfolio Management" onClose={onClose}>
        <div className="font-mono text-mono-tag uppercase text-danger">Could not load portfolio data</div>
      </DetailShell>
    );
  }
  if (!clients || !seasons) {
    return (
      <DetailShell eyebrow="06 · Delivery Dashboard" title="Portfolio Management" onClose={onClose}>
        <div className="font-mono text-mono-tag uppercase text-ink-3">Loading…</div>
      </DetailShell>
    );
  }

  return (
    <DetailShell eyebrow="06 · Delivery Dashboard" title="Portfolio Management" onClose={onClose}>
      <Card>
        <SectionTitle label="Active clients" meta="ACQUIRE / HOLD / RELEASE" />
        <Table>
          <thead>
            <tr>
              <Th>Client</Th>
              <Th>Plan</Th>
              <Th>Theme focus</Th>
              <Th>Mood</Th>
              <Th>Since</Th>
              <Th>Ends</Th>
              <Th>Note</Th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.id} className="hover:bg-paper-2">
                <Td className="font-serif" style={{ fontSize: 15 }}>{c.company}</Td>
                <Td className="font-mono">{c.plan}</Td>
                <Td>{c.theme_focus}</Td>
                <Td>
                  <Chip style={{ background: MOOD_BG[c.mood] || 'var(--paper-2)' }}>{c.mood}</Chip>
                </Td>
                <Td className="font-mono">{formatStart(c.started_at)}</Td>
                <Td className="font-mono">{c.end_label}</Td>
                <Td className="text-ink-3">{c.note}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <div className="grid grid-cols-2 gap-4">
        <Card>
          <SectionTitle label="Seasons & rhythms" meta="12-MONTH LOAD" />
          <div
            className="grid items-end h-[140px]"
            style={{ gridTemplateColumns: 'repeat(12, 1fr)', gap: 4 }}
          >
            {seasons.map((s) => (
              <div key={s.id} className="flex flex-col items-center gap-1">
                <div
                  className="w-full border border-ink"
                  style={{
                    height: `${s.load * 100}%`,
                    background: s.load > 0.9 ? 'var(--accent)' : 'var(--ink)',
                  }}
                />
                <div className="font-mono text-[9px] uppercase tracking-[0.06em] text-ink-3">
                  {s.month_label}
                </div>
              </div>
            ))}
          </div>
          <div className="grid gap-1 mt-2.5">
            {seasons.filter((s) => s.spike).map((s) => (
              <div key={s.id} className="flex gap-2 text-[12px]">
                <span className="font-mono">{s.month_label.toUpperCase()}</span>
                <span className="text-accent-ink">▲ {s.spike}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <SectionTitle label="Resource building" meta="WHEN TO HAND OVER" />
          <div className="grid gap-2.5">
            <div className="px-2.5 py-1" style={{ borderLeft: '2px solid var(--ink)' }}>
              <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-3">HARROW &amp; FEN</div>
              <div className="text-[13px]">Full-time COO search — Week 6 of 14. Shortlist by 04 May.</div>
            </div>
            <div className="px-2.5 py-1" style={{ borderLeft: '2px solid var(--hairline)' }}>
              <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-3">SALTER &amp; KIN</div>
              <div className="text-[13px]">Not yet — founder isn't ready to hire. Revisit Q4.</div>
            </div>
            <div className="px-2.5 py-1" style={{ borderLeft: '2px solid var(--hairline)' }}>
              <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-3">LINDEN LOOP</div>
              <div className="text-[13px]">Keep — role is ongoing, board prefers fractional.</div>
            </div>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-ink-3 mt-2">
            Coach: "You rescue founders; you don't rescue roles. The difference is who needs to grow."
          </div>
        </Card>
      </div>
    </DetailShell>
  );
}

function formatStart(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

export default PortfolioDetail;
